import React, { useEffect, useMemo, useState } from "react";
import UdrzbaDetailZakazky from "./UdrzbaDetailZakazky";
import { apiJson } from "../../auth/authClient";

const API = "/api/udrzba";

function formatDate(iso) {
  const s = String(iso || "").slice(0, 10);
  if (!s) return "-";
  const [y, m, d] = s.split("-");
  if (!y || !m || !d) return s;
  return `${d}.${m}.${y}`;
}

function badgeStyle(type) {
  const base = {
    display: "inline-block",
    padding: "4px 8px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    border: "1px solid transparent",
    whiteSpace: "nowrap",
  };

  if (type === "today") return { ...base, background: "#ecfeff", color: "#155e75", borderColor: "#a5f3fc" };
  if (type === "overdue") return { ...base, background: "#fef2f2", color: "#991b1b", borderColor: "#fecaca" };
  if (type === "linked") return { ...base, background: "#ecfdf5", color: "#065f46", borderColor: "#a7f3d0" };
  if (type === "predplatne") return { ...base, background: "#eff6ff", color: "#1d4ed8", borderColor: "#bfdbfe" };
  return { ...base, background: "#f3f4f6", color: "#374151", borderColor: "#d1d5db" };
}

function typPeceLabel(v) {
  const x = String(v || "").trim().toLowerCase();
  if (x === "jednorazova") return "Jednorázová";
  if (x === "predplatne") return "Předplatné";
  if (x === "balicek" || x === "balicek_4x") return "Balíček";
  return v || "-";
}

function kpiCardStyle(active) {
  if (type === "manual") {
    return {
      display: "inline-flex",
      alignItems: "center",
      padding: "4px 8px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      background: "#fff7ed",
      color: "#c2410c",
      border: "1px solid #fdba74",
      whiteSpace: "nowrap",
    };
  }

  return {
    border: active ? "1px solid #2563eb" : "1px solid #e5e7eb",
    borderRadius: 14,
    padding: "10px 12px",
    background: active ? "#eff6ff" : "#fff",
    cursor: "pointer",
    boxShadow: active ? "0 0 0 3px rgba(37, 99, 235, 0.12)" : "none",
  };
}

function filterBtnStyle(active) {
  return {
    padding: "8px 12px",
    borderRadius: 999,
    border: active ? "1px solid #111827" : "1px solid #d1d5db",
    background: active ? "#111827" : "#fff",
    color: active ? "#fff" : "#111827",
    fontWeight: 700,
    fontSize: 13,
    cursor: "pointer",
  };
}

function isSameWeek(dateIso, todayIso) {
  const a = new Date(String(dateIso || "").slice(0, 10));
  const b = new Date(String(todayIso || "").slice(0, 10));
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return false;

  const day = (d) => {
    const x = d.getDay();
    return x === 0 ? 7 : x;
  };

  const startOfWeek = (d) => {
    const copy = new Date(d);
    copy.setHours(0, 0, 0, 0);
    copy.setDate(copy.getDate() - (day(copy) - 1));
    return copy;
  };

  const startA = startOfWeek(a).getTime();
  const startB = startOfWeek(b).getTime();
  return startA === startB;
}

function stavLabel(v) {
  const x = String(v || "").trim().toLowerCase();
  if (x === "nova") return "Nová";
  if (x === "naplanovano") return "Naplánováno";
  if (x === "dokonceno") return "Dokončeno";
  if (x === "vyfakturovano") return "Vyfakturováno";
  if (x === "zruseno") return "Zrušeno";
  return v || "-";
}

export default function UdrzbaKalendar({ setSection }) {
  const [items, setItems] = useState([]);
  const [today, setToday] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState(null);
  const [quickFilter, setQuickFilter] = useState(() => {
    try {
      const saved = localStorage.getItem("udrzba_kalendar_quick_filter");
      return saved || "vse";
    } catch {
      return "vse";
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("udrzba_kalendar_quick_filter", quickFilter);
    } catch {}
  }, [quickFilter]);
  const [viewMode, setViewMode] = useState(() => {
    try {
      const saved = localStorage.getItem("udrzba_kalendar_view_mode");
      return saved === "list" ? "list" : "grouped";
    } catch {
      return "grouped";
    }
  });
  const [hideDone, setHideDone] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => {
    try {
      return localStorage.getItem("udrzba_kalendar_selected_date") || "";
    } catch {
      return "";
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("udrzba_kalendar_selected_date", selectedDate);
    } catch {}
  }, [selectedDate]);

  useEffect(() => {
    try {
      localStorage.setItem("udrzba_kalendar_view_mode", viewMode);
    } catch {}
  }, [viewMode]);

  useEffect(() => {
    let alive = true;

    async function load() {
      setLoading(true);
      setError("");
      try {
        const data = await apiJson(`${API}/kalendar`, { method: "GET" });
        if (!data?.ok) throw new Error(data?.error || "Načtení kalendáře selhalo");

        if (!alive) return;
        setItems(Array.isArray(data.items) ? data.items : []);
        setToday(String(data.today || ""));
      } catch (e) {
        if (!alive) return;
        setItems([]);
        setError(e?.message || "Načtení kalendáře selhalo");
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, []);

  const filteredItems = useMemo(() => {
    let base = Array.isArray(items) ? [...items] : [];

    if (hideDone) {
      base = base.filter((x) => String(x?.stav || "").trim().toLowerCase() !== "dokonceno");
    }

    if (quickFilter === "dnes") {
      return base.filter((x) => x.isToday);
    }
    if (quickFilter === "po_terminu") {
      return base.filter((x) => x.isOverdue);
    }
    if (quickFilter === "zapsano") {
      return base.filter((x) => x.calendarLinked);
    }
    if (quickFilter === "predplatne") {
      return base.filter((x) => {
        const t = String(x?.typPece || "").trim().toLowerCase();
        return t === "predplatne" || t === "balicek" || t === "balicek_4x";
      });
    }
    if (quickFilter === "dokoncene") {
      return base.filter((x) => String(x?.stav || "").trim().toLowerCase() === "dokonceno");
    }

    return base;
  }, [items, quickFilter, hideDone]);

  const tomorrowDate = useMemo(() => {
    const base = new Date(String(today || "").slice(0, 10));
    if (Number.isNaN(base.getTime())) return "";
    base.setDate(base.getDate() + 1);
    return base.toISOString().slice(0, 10);
  }, [today]);

  const dateFilteredItems = useMemo(() => {
    const picked = String(selectedDate || "").trim();
    if (!picked) return filteredItems;
    if (picked === "__THIS_WEEK__") {
      return filteredItems.filter((x) => isSameWeek(String(x?.date || "").slice(0, 10), today));
    }
    return filteredItems.filter((x) => String(x?.date || "").slice(0, 10) === picked);
  }, [filteredItems, selectedDate, today]);

  const sortedItems = useMemo(() => {
    const arr = [...dateFilteredItems];
    const todayKey = String(today || "").slice(0, 10);

    const rank = (dateStr) => {
      const d = String(dateStr || "").slice(0, 10);
      if (!d) return 999999999;
      if (d === todayKey) return -1;
      const t = new Date(d).getTime();
      const todayTs = new Date(todayKey).getTime();
      if (Number.isNaN(t) || Number.isNaN(todayTs)) return 999999999;
      if (t > todayTs) return t - todayTs;
      return 1000000000 + (todayTs - t);
    };

    arr.sort((a, b) => {
      const ar = rank(a?.date);
      const br = rank(b?.date);
      if (ar !== br) return ar - br;

      const av = String(a?.objednavatel || "").toLocaleLowerCase("cs");
      const bv = String(b?.objednavatel || "").toLocaleLowerCase("cs");
      return av.localeCompare(bv, "cs");
    });

    return arr;
  }, [dateFilteredItems, today]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const item of sortedItems) {
      const key = String(item?.date || "").slice(0, 10);
      if (!key) continue;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(item);
    }

    const todayKey = String(today || "").slice(0, 10);
    const rank = (dateStr) => {
      const d = String(dateStr || "").slice(0, 10);
      if (!d) return 999999999;
      if (d === todayKey) return -1;
      const t = new Date(d).getTime();
      const todayTs = new Date(todayKey).getTime();
      if (Number.isNaN(t) || Number.isNaN(todayTs)) return 999999999;
      if (t > todayTs) return t - todayTs;
      return 1000000000 + (todayTs - t);
    };

    return Array.from(map.entries()).sort((a, b) => rank(a[0]) - rank(b[0]));
  }, [sortedItems, today]);

  function goToZakazkyFilter(filter) {
    try {
      sessionStorage.setItem("udrzba_calendar_jump_filter", String(filter || "vse"));
    } catch {}
    if (typeof setSection === "function") {
      setSection("zakazky");
    }
  }

  function goToZakazkyDate(date) {
    try {
      sessionStorage.setItem("udrzba_calendar_jump_date", String(date || ""));
      sessionStorage.removeItem("udrzba_calendar_jump_filter");
    } catch {}
    if (typeof setSection === "function") {
      setSection("zakazky");
    }
  }

  async function quickDone(id) {
  try {
    const data = await apiJson(`${API}/zakazky/${id}/dokonceno`, { method: "POST", body: {} });
    if (!data?.ok) throw new Error(data?.error || "Chyba dokončení");

    const d = await apiJson(`${API}/kalendar`, { method: "GET" });
    if (d?.ok) {
      setItems(Array.isArray(d.items) ? d.items : []);
      setToday(String(d.today || ""));
    }
  } catch (e) {
    alert(e?.message || "Nepodařilo se označit jako hotové");
  }
}

async function openDetail(id) {
    try {
      const data = await apiJson(`${API}/zakazky/${id}`, { method: "GET" });
      if (!data?.ok || !data?.item) {
        throw new Error(data?.error || "Nepodařilo se otevřít detail zakázky.");
      }
      setDetail(data.item);
    } catch (e) {
      alert(e?.message || "Nepodařilo se otevřít detail zakázky.");
    }
  }

  function exportCalendarCsv() {
    try {
      const rows = dateFilteredItems.map((x) => ({
        datum: formatDate(x.date),
        datum_iso: String(x.date || "").slice(0, 10),
        nazev: x.title || "",
        hrbitov: x.hrbitov || "",
        cislo: x.cislo || "",
        typ_pece: typPeceLabel(x.typPece),
        stav: stavLabel(x.stav),
        objednavatel: x.objednavatel || "",
        poradi_navstevy: Number(x.navstevaPoradi || 1),
        zbyva_navstev: Number(x.zbyvaNavstev || 0),
        navstev_celkem: Number(x.pocetNavstevCelkem || 0),
        stav_predplatneho: x.stavPredplatneho || "",
        dnes: x.isToday ? "ano" : "",
        po_terminu: x.isOverdue ? "ano" : "",
        kalendar: x.calendarLinked ? "ano" : "",
      }));

      const headers = [
        "datum",
        "datum_iso",
        "nazev",
        "hrbitov",
        "cislo",
        "typ_pece",
        "stav",
        "objednavatel",
        "poradi_navstevy",
        "zbyva_navstev",
        "navstev_celkem",
        "stav_predplatneho",
        "dnes",
        "po_terminu",
        "kalendar",
      ];

      const escapeCell = (v) => {
        const text = String(v ?? "");
        return `"${text.replace(/"/g, '""')}"`;
      };

      const csv = [
        headers.join(";"),
        ...rows.map((row) => headers.map((h) => escapeCell(row[h])).join(";")),
      ].join("\n");

      const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const suffix = String(selectedDate || quickFilter || "vse").replace(/[^a-zA-Z0-9_-]+/g, "_");
      a.href = url;
      a.download = `udrzba-kalendar-${suffix}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e?.message || "Export CSV se nepodařil.");
    }
  }

  const stats = useMemo(() => {
    return {
      total: dateFilteredItems.length,
      today: dateFilteredItems.filter((x) => x.isToday).length,
      tomorrow: dateFilteredItems.filter((x) => String(x?.date || "").slice(0, 10) === tomorrowDate).length,
      thisWeek: dateFilteredItems.filter((x) => isSameWeek(String(x?.date || "").slice(0, 10), today)).length,
      overdue: dateFilteredItems.filter((x) => x.isOverdue).length,
      linked: dateFilteredItems.filter((x) => x.calendarLinked).length,
    };
  }, [dateFilteredItems, tomorrowDate, today]);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap", marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0 }}>Kalendář údržby</h2>
          <div style={{ color: "#6b7280", marginTop: 6 }}>
            Přehled zakázek podle plánovaného data / další návštěvy
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <div style={{ border: "1px solid #e5e7eb", borderRadius: 14, padding: "10px 12px", background: "#fff" }}>
            <div style={{ fontSize: 12, color: "#6b7280" }}>Celkem</div>
            <div style={{ fontSize: 22, fontWeight: 800 }}>{stats.total}</div>
          </div>
          <div style={{ border: "1px solid #e5e7eb", borderRadius: 14, padding: "10px 12px", background: "#fff" }}>
            <div style={{ fontSize: 12, color: "#6b7280" }}>Dnes</div>
            <div style={{ fontSize: 22, fontWeight: 800 }}>{stats.today}</div>
          </div>
          <div style={{ border: "1px solid #e5e7eb", borderRadius: 14, padding: "10px 12px", background: "#fff" }}>
            <div style={{ fontSize: 12, color: "#6b7280" }}>Po termínu</div>
            <div style={{ fontSize: 22, fontWeight: 800 }}>{stats.overdue}</div>
          </div>
          <div style={{ border: "1px solid #e5e7eb", borderRadius: 14, padding: "10px 12px", background: "#fff" }}>
            <div style={{ fontSize: 12, color: "#6b7280" }}>Zapsáno v kalendáři</div>
            <div style={{ fontSize: 22, fontWeight: 800 }}>{stats.linked}</div>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 16, color: "#6b7280", fontSize: 14 }}>
        Dnes: <strong>{formatDate(today)}</strong>
      </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 16 }}>
          <button
            type="button"
            onClick={exportCalendarCsv}
            style={filterBtnStyle(false)}
          >
            Export CSV
          </button>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            style={{
              border: selectedDate ? "1px solid #2563eb" : "1px solid #d1d5db",
              borderRadius: 10,
              padding: "8px 10px",
              fontSize: 14,
              background: selectedDate ? "#eff6ff" : "#fff",
              boxShadow: selectedDate ? "0 0 0 3px rgba(37, 99, 235, 0.12)" : "none",
            }}
          />
          <button
            type="button"
            onClick={() => setSelectedDate(String(today || "").slice(0, 10))}
            style={filterBtnStyle(selectedDate === String(today || "").slice(0, 10))}
          >
            Dnes
          </button>
          <button
            type="button"
            onClick={() => setSelectedDate(tomorrowDate)}
            style={filterBtnStyle(selectedDate === tomorrowDate)}
          >
            Zítra
          </button>
          <button
            type="button"
            onClick={() => setSelectedDate("__THIS_WEEK__")}
            style={filterBtnStyle(selectedDate === "__THIS_WEEK__")}
          >
            Tento týden
          </button>
          <button
            type="button"
            onClick={() => setSelectedDate("")}
            style={filterBtnStyle(!selectedDate)}
          >
            Vyčistit datum
          </button>
          {selectedDate ? (
            <div
              style={{
                color: "#1d4ed8",
                fontSize: 14,
                background: "#eff6ff",
                border: "1px solid #bfdbfe",
                borderRadius: 999,
                padding: "6px 10px",
                fontWeight: 600,
              }}
            >
              {selectedDate === "__THIS_WEEK__" ? (
                <>Vybraný rozsah: <strong>Tento týden</strong></>
              ) : (
                <>Vybraný den: <strong>{formatDate(selectedDate)}</strong></>
              )}
            </div>
          ) : null}
        </div>


        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
          <button
            type="button"
            onClick={() => setViewMode("grouped")}
            style={filterBtnStyle(viewMode === "grouped")}
          >
            Skupiny
          </button>
          <button
            type="button"
            onClick={() => setViewMode("list")}
            style={filterBtnStyle(viewMode === "list")}
          >
            Seznam
          </button>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
          <button type="button" onClick={() => setQuickFilter("vse")} style={filterBtnStyle(quickFilter === "vse")}>
            Vše
          </button>
          <button type="button" onClick={() => setQuickFilter("dnes")} style={filterBtnStyle(quickFilter === "dnes")}>
            Dnes
          </button>
          <button type="button" onClick={() => setQuickFilter("po_terminu")} style={filterBtnStyle(quickFilter === "po_terminu")}>
            Po termínu
          </button>
          <button type="button" onClick={() => setQuickFilter("zapsano")} style={filterBtnStyle(quickFilter === "zapsano")}>
            Zapsáno v kalendáři
          </button>
          <button type="button" onClick={() => setQuickFilter("predplatne")} style={filterBtnStyle(quickFilter === "predplatne")}>
            Předplatné
          </button>
            <button type="button" onClick={() => setQuickFilter("dokoncene")} style={filterBtnStyle(quickFilter === "dokoncene")}>
              Jen dokončené
            </button>
            <button type="button" onClick={() => setHideDone((v) => !v)} style={filterBtnStyle(hideDone)}>
              {hideDone ? "Zobrazit dokončené" : "Skrýt dokončené"}
            </button>
        </div>

        <div style={{ marginBottom: 16, color: "#6b7280", fontSize: 14 }}>
          Zobrazeno: <strong>{filteredItems.length}</strong> z <strong>{items.length}</strong>
        </div>
      {loading ? (
        <div style={{ padding: 20, border: "1px solid #e5e7eb", borderRadius: 16, background: "#fff" }}>
          Načítám kalendář…
        </div>
      ) : error ? (
        <div style={{ padding: 20, border: "1px solid #fecaca", borderRadius: 16, background: "#fef2f2", color: "#991b1b" }}>
          {error}
        </div>
      ) : !grouped.length ? (
        <div style={{ padding: 20, border: "1px solid #e5e7eb", borderRadius: 16, background: "#fff" }}>
          V kalendáři zatím nejsou žádné položky.
        </div>
        ) : viewMode === "list" ? (
          <div style={{ display: "grid", gap: 10 }}>
            {sortedItems.map((x) => { const canQuickDone = !["dokonceno", "vyfakturovano", "zruseno"].includes(String(x?.stav || "").trim().toLowerCase()); return (
              <div
                key={x.id}
                onClick={() => openDetail(x.id)}
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 14,
                  padding: 14,
                  background: x.isOverdue ? "#fffafa" : "#fff",
                  cursor: "pointer",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start", flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 16 }}>{x.title || "-"}</div>
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        goToZakazkyDate(x.date);
                      }}
                      style={{ color: "#6b7280", marginTop: 4, fontSize: 14, cursor: "pointer" }}
                    >
                      {formatDate(x.date)} • {x.hrbitov || "-"} {x.cislo ? `• ${x.cislo}` : ""}
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                    {x.isToday ? <span style={badgeStyle("today")}>Dnes</span> : null}
                    {x.isOverdue ? <span style={badgeStyle("overdue")}>Po termínu</span> : null}
                    {x.calendarLinked ? <span style={badgeStyle("linked")}>Kalendář</span> : null}
                      {String(x?.stav || "").trim().toLowerCase() === "dokonceno" ? <span style={badgeStyle("done")}>Dokončeno</span> : null}
                    {x.rucneUpravenyTermin ? <span style={badgeStyle("manual")}>Ručně upraveno</span> : null}
                    <span style={badgeStyle("predplatne")}>{typPeceLabel(x.typPece)}</span>
                  
                      {!["dokonceno", "vyfakturovano", "zruseno"].includes(String(x?.stav || "").trim().toLowerCase()) ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (confirm("Opravdu označit jako hotové?")) {
                            if (confirm("Opravdu označit jako hotové?")) quickDone(x.id);
                          }
                          }}
                          style={{
                            padding: "6px 10px",
                            borderRadius: 10,
                            border: "1px solid #16a34a",
                            background: "#ecfdf5",
                            color: "#166534",
                            fontWeight: 700,
                            cursor: "pointer"
                          }}
                        >
                          Označit jako hotové
                        </button>
                      ) : null}
</div>
                </div>
              </div>
            ); })}
          </div>
        ) : (
        <div style={{ display: "grid", gap: 14 }}>
            {grouped.map(([date, rows]) => {
              const hasOverdue = rows.some((x) => x.isOverdue);
              const isTodayBlock = date === today;
                const headerBorder = isTodayBlock
                  ? "1px solid #bae6fd"
                  : hasOverdue
                  ? "1px solid #fecaca"
                  : "1px solid #e5e7eb";
                const headerBg = isTodayBlock
                  ? "#eff6ff"
                  : hasOverdue
                  ? "#fff1f2"
                  : "#f8fafc";

              return (
                <div
                  key={date}
                  style={{
                    border: isTodayBlock ? "1px solid #7dd3fc" : hasOverdue ? "1px solid #fecaca" : "1px solid #e5e7eb",
                    borderRadius: 18,
                    background: "#fff",
                    overflow: "hidden",
                    boxShadow: isTodayBlock
                      ? "0 10px 24px rgba(14, 165, 233, 0.12)"
                      : hasOverdue
                      ? "0 8px 20px rgba(239, 68, 68, 0.08)"
                      : "none",
                  }}
                >
                  <div
                      onClick={() => goToZakazkyDate(date)}
                      style={{
                        padding: "14px 16px",
                        borderBottom: headerBorder,
                        background: headerBg,
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 12,
                        alignItems: "center",
                        flexWrap: "wrap",
                        cursor: "pointer",
                      }}
                    >
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <div style={{ fontWeight: 800, fontSize: 18 }}>{formatDate(date)}</div>
                      {isTodayBlock ? <span style={badgeStyle("today")}>Dnes</span> : null}
                      {!isTodayBlock && hasOverdue ? <span onClick={(e) => { e.stopPropagation(); goToZakazkyFilter("po_terminu"); }} style={{ ...badgeStyle("overdue"), cursor: "pointer" }}>Po termínu</span> : null}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <div onClick={(e) => { e.stopPropagation(); goToZakazkyDate(date); }} style={{ fontSize: 13, color: "#6b7280", cursor: "pointer" }}>{rows.length} položek</div>
                      {rows.some((x) => x.calendarLinked) ? <span onClick={(e) => { e.stopPropagation(); goToZakazkyFilter("zapsano"); }} style={{ ...badgeStyle("linked"), cursor: "pointer" }}>Má kalendář</span> : null}
                    </div>
                  </div>

                  <div style={{ padding: 8 }}>
                    {rows.map((x) => (
                      <div
                        key={x.id}
                        onClick={() => openDetail(x.id)}
                        style={{
                          border: "1px solid #f1f5f9",
                          borderRadius: 14,
                          padding: 14,
                          marginBottom: 8,
                          background: x.isOverdue ? "#fffafa" : "#fff",
                          cursor: "pointer",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start", flexWrap: "wrap" }}>
                          <div>
                            <div style={{ fontWeight: 800, fontSize: 16 }}>{x.title || "-"}</div>
                            <div style={{ color: "#6b7280", marginTop: 4, fontSize: 14 }}>
                              {x.hrbitov || "-"} {x.cislo ? `• ${x.cislo}` : ""}
                            </div>
                          </div>

                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                            {x.isToday ? <span style={badgeStyle("today")}>Dnes</span> : null}
                            {x.isOverdue ? <span style={badgeStyle("overdue")}>Po termínu</span> : null}
                            {x.calendarLinked ? <span style={badgeStyle("linked")}>Kalendář</span> : null}
                            {x.rucneUpravenyTermin ? <span style={badgeStyle("manual")}>Ručně upraveno</span> : null}
                            <span style={badgeStyle("predplatne")}>{typPeceLabel(x.typPece)}</span>
                          </div>
                        </div>

                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                            gap: 10,
                            marginTop: 12,
                            fontSize: 14,
                          }}
                        >
                          <div>
                            <div style={{ color: "#6b7280", fontSize: 12 }}>Stav</div>
                            <div style={{ fontWeight: 600 }}>{stavLabel(x.stav)}</div>
                          </div>

                          <div>
                            <div style={{ color: "#6b7280", fontSize: 12 }}>Objednavatel</div>
                            <div style={{ fontWeight: 600 }}>{x.objednavatel || "-"}</div>
                          </div>

                          <div>
                            <div style={{ color: "#6b7280", fontSize: 12 }}>Pořadí návštěvy</div>
                            <div style={{ fontWeight: 600 }}>{Number(x.navstevaPoradi || 1)}</div>
                          </div>

                          <div>
                            <div style={{ color: "#6b7280", fontSize: 12 }}>Zbývá návštěv</div>
                            <div style={{ fontWeight: 600 }}>
                              {Number(x.zbyvaNavstev || 0)} / {Number(x.pocetNavstevCelkem || 0)}
                            </div>
                          </div>

                          <div>
                            <div style={{ color: "#6b7280", fontSize: 12 }}>Stav předplatného</div>
                            <div style={{ fontWeight: 600 }}>{x.stavPredplatneho || "-"}</div>
                          </div>

                          <div>
                            <div style={{ color: "#6b7280", fontSize: 12 }}>Datum</div>
                            <div style={{ fontWeight: 600 }}>{formatDate(x.date)}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
        </div>
      )}
        {detail ? (
          <UdrzbaDetailZakazky
            item={detail}
            onClose={() => setDetail(null)}
            onSaved={(savedItem) => setDetail(savedItem)}
          />
        ) : null}
    </div>
  );
}
